export const ggVars: constants.GGVar[] = [
  {
    label: 'Wet',
    key: 'WET',
    idx: 1,
  },
  {
    label: 'Roasted',
    key: 'ROASTED',
    idx: 2,
  },
  {
    label: 'Earthy',
    key: 'EARTHY',
    idx: 3,
  },
  {
    label: 'Astringent',
    key: 'ASTRINGENT',
    idx: 4,
  },
  {
    label: 'Marine',
    key: 'MARINE',
    idx: 5,
  },
  {
    label: 'Mineral',
    key: 'MINERAL',
    idx: 6,
  },
  {
    label: 'Retronasal',
    key: 'RETRONASAL',
    idx: 7,
  },
  {
    label: 'Gamey',
    key: 'GAMEY',
    idx: 8,
  },
  {
    label: 'Woody',
    key: 'WOODY',
    idx: 9,
  },
  {
    label: 'Bitter',
    key: 'BITTER',
    idx: 10,
  },
  {
    label: 'Meaty',
    key: 'MEATY',
    idx: 11,
  },
  {
    label: 'Smoked',
    key: 'SMOKED',
    idx: 12,
  },
  {
    label: 'Dry',
    key: 'DRY',
    idx: 13,
  },
  {
    label: 'Rich',
    key: 'RICH',
    idx: 14,
  },
  {
    label: 'Dairy',
    key: 'DAIRY',
    idx: 15,
  },
  {
    label: 'Mouth Feel',
    key: 'MOUTHFEEL',
    idx: 16,
  },
  {
    label: 'Nuts & Seeds',
    key: 'NUTS_AND_SEEDS',
    idx: 17,
  },

  {
    label: 'Herbaceous',
    key: 'HERBACEOUS',
    idx: 18,
  },

  {
    label: 'Cold Finish',
    key: 'COLD_FINISH',
    idx: 19,
  },

  {
    label: 'Floral',
    key: 'FLORAL',
    idx: 20,
  },

  {
    label: 'Spices',
    key: 'SPICES',
    idx: 21,
  },

  {
    label: 'Sour & Acidity',
    key: 'SOUR_AND_ACIDITY',
    idx: 22,
  },

  {
    label: 'Fruits',
    key: 'FRUITS',
    idx: 23,
  },

  {
    label: 'Sugar',
    key: 'SUGAR',
    idx: 24,
  },
];

export interface GgVarAttributes {
  WET: number;
  ROASTED: number;
  EARTHY: number;
  ASTRINGENT: number;
  MARINE: number;
  MINERAL: number;
  RETRONASAL: number;
  GAMEY: number;
  WOODY: number;
  BITTER: number;
  MEATY: number;
  SMOKED: number;
  DRY: number;
  RICH: number;
  DAIRY: number;
  MOUTHFEEL: number;
  NUTS_AND_SEEDS: number;
  HERBACEOUS: number;
  COLD_FINISH: number;
  FLORAL: number;
  SPICES: number;
  SOUR_AND_ACIDITY: number;
  FRUITS: number;
  SUGAR: number;
}

export interface GGVarLabelMapping {
  [key: string]: string;
}

const labelMapping: GGVarLabelMapping = ggVars.reduce((accu, val) => {
  accu[val.key] = val.label;
  return accu;
}, {});

export const mapLabel = function (val: string) {
  const label = labelMapping[val];
  return typeof label !== 'undefined' && label !== null ? label : val;
};

/**
 *
 * @param key GGVar key from the database. Ought to be typed as gastrograph.ggvar_enum
 * There should be only one way to write each GGVar in the database.
 * This makes the string matching far easier.
 * Note that postgraphile uppercases enum values, and turns camel case to snake case.
 * @example mapFromKey('SOUR_AND_ACIDITY') => { label: 'Sour & Acidity', key: 'SOUR_AND_ACIDITY', idx: 22 },
 * @returns The client-friendly display form of each GGVar.
 */
export const mapFromKey: (key: string) => constants.GGVar = (
  key: constants.GGVarEnum,
) => {
  const exactMatch = ggVars.find((ggVar) => ggVar.key === key);
  if (exactMatch) {
    return exactMatch;
  } else {
    console.error('Invalid key: ', key);
    throw new Error('GGVar key did not abide ggvar_enum type in the database');
  }
};

/**
 * This is needed because gastrograph.rp_ofp attributes is a string column that can handle both string ReffFlavors and GGVars.
 * The GGVars are valid postgres ggvar_enum values BUT Postgraphile will not map them to the GraphQL Enum equivalent since it is a string column.
 * So this is necessary, unless we can disable Postgraphile ENUM casting, which does not appear straight forward.
 * @param str Something like nutsAndSeeds
 * @returns NUTS_AND_SEEDS for instance
 */
export const toUpperSnakeCase: (str: string) => constants.GGVarEnum = (str) =>
  str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert underscore between lowercase and uppercase
    .toUpperCase() as constants.GGVarEnum; // Convert to uppercase

/**
 * This is needed because GastrographPackage expects snake casing.
 * Used in report ordering when selecting GGVars to constraint for Optimizations
 * @param str Something like NUTS_AND_SEEDS
 * @returns nutsAndSeeds
 */
export const toCamelCase: (GGVarKey: constants.GGVarEnum) => string = (GGVarKey) =>
  GGVarKey
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()); // NUTS_AND to nutsAnd

/**
 * Data Quality View columns are using oldDataQualityKeys.
 * However, we are expecting GraphQL Enum cases in ggVar constants (refer above)
 * This function will replace those GGVar columns with GraphQL Enum casing.
 * */
export const fixDataQualityDashboardGGVars: (
  dataQualityRow: dataquality.PanelDataQualityQueryRow,
) => dataquality.PanelDataQualityQueryRow_GQL_ENUM = (dataQualityRow) => {
  const oldDataQualityKeys = [
    'astringent',
    'bitter',
    'coldFinish',
    'dairy',
    'dry',
    'earthy',
    'floral',
    'fruits',
    'gamey',
    'herbaceous',
    'marine',
    'meaty',
    'mineral',
    'mouthfeel',
    'nutsAndSeeds',
    'retronasal',
    'rich',
    'roasted',
    'smoked',
    'sourAndAcidity',
    'spices',
    'sugar',
    'wet',
    'woody',
  ];
  return Object.fromEntries(
    Object.entries(dataQualityRow).map(([k, v]) =>
      [oldDataQualityKeys.includes(k) ? toUpperSnakeCase(k) : k, v]
    )
  ) as dataquality.PanelDataQualityQueryRow_GQL_ENUM
};

export default ggVars;